/**
 * The price of the click, shown before the click.
 *
 * At rest an action row shows its keyboard chip; hovering swaps it for
 * "$0.67 → $0.82" — what the exploration has drawn, and what it is expected to
 * stand at after this press. The swap is pure CSS (`.action:hover`), so no
 * hover state threads through every button that wants one.
 *
 * The projection is `projectVerbUsd`, the same per-move figure the run
 * pre-flight quote is built from — one arithmetic, so the hover on Deepen and
 * the quote on a 24-move run cannot disagree. A scan is the one exception: its
 * input is its own prompt, not a move's, so it is priced from that prompt
 * (`closingScanUsd`), as the Overview's press is. The figure leans high at list,
 * like everything else with a dollar sign in it, and turns tension-red when the
 * press would carry the exploration past the cap typed in the composer. No list
 * rate for the routed model → no figure, rather than a wrong one.
 *
 * And no key on the route the press runs on → no figure either, but a word:
 * `instead` is the status word the caller's press is named with where it will
 * not spend ("Needs a key", "Part of the demo"), printed in the figure's place
 * and shown and hidden by the figure's own rules. The cell never reads a key
 * itself — a press knows its route (`routeReady`, `actReady`) and the cell does
 * not — so the caller says; the caption over the six verbs printed a figure for
 * a routed role with no key while the verbs' names said "needs a key".
 */

import { useMemo } from 'react'
import { useStore } from '../core/store'
import { estimateSpendUsd, fmtUsd, type ModelEntry } from '../core/models'
import { closingScanUsd, pressBasis, projectVerbUsd } from '../engine/run'
import { useVoiceOn } from './useVoiceOn'
import { useFigureBasis } from './useFigureBasis'
import { parseCap } from '../core/ledger'
import { STATUS_WORD } from '../core/vocabulary'

export function VerbPrice({
  verb,
  kbd,
  auditor,
  count = 1,
  instead,
  inRun = false,
}: {
  /** `scan` appears on exactly one surface — the run gate pricing a proposed
   *  closing scan — and is priced from the prompt that scan would send
   *  (`closingScanUsd`), never as a move: its input is its prompt, and on a
   *  large graph a move's figure was the low one. */
  verb: 'move' | 'verify' | 'priorArt' | 'sweep' | 'panel' | 'audit' | 'scan'
  /** The resting keyboard chip, when the action has one. */
  kbd?: string
  /** The model an audit would run on — its price is another house's. */
  auditor?: ModelEntry | null
  count?: number
  /** Where the press will not spend: the word to print in the figure's place, or
   *  null for nothing at all. Omitted, the figure. */
  instead?: string | null
  /** A move a run is waiting to make, at its gate: the run is quiet until it
   *  ends, so the figure is the pass alone, without the voice's line (D295). */
  inRun?: boolean
}) {
  const spend = useStore((s) => s.session.spend)
  const rev = useStore((s) => s.rev)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const runCeiling = useStore((s) => s.runCeiling)

  const before = useMemo(() => estimateSpendUsd(spend).usd, [spend, rev])
  // What the scan reads, subscribed only where a scan is priced: every other
  // verb's figure is a move's and does not move with the graph.
  const scanSession = useStore((s) => (verb === 'scan' ? s.session : null))
  const lens = useStore((s) => s.lens)
  const temper = useStore((s) => s.temper)
  const scanUsd = useMemo(
    () =>
      scanSession
        ? closingScanUsd({ modelId, roleModels, lens, temper, session: scanSession })
        : null,
    [scanSession, modelId, roleModels, lens, temper],
  )
  // Heard so the figure moves when the voice's switch does: a press made outside
  // a run is quoted with the voice's line after it while the voice is on (D295).
  // A run's gate is not — the run is quiet until it ends — and neither is the
  // scan, which is only ever priced there.
  useVoiceOn()
  useFigureBasis()
  const est =
    verb === 'scan'
      ? scanUsd
      : projectVerbUsd({ modelId, roleModels }, verb, auditor, { voice: !inRun, count })
  const cap = parseCap(runCeiling)

  if (instead !== undefined) {
    if (!instead && !kbd) return null
    return (
      <span className="vp">
        {kbd && <span className="kbd vp-kbd">{kbd}</span>}
        {instead && <span className="vp-price vp-word">{instead}</span>}
      </span>
    )
  }
  if (est === null) return kbd ? <span className="kbd vp-kbd">{kbd}</span> : null

  const after = before + est
  const over = cap !== null && after > cap
  // What the figure is drawn from, said beside it (D311): this browser's own
  // passes on the routed model once enough have run, the allowance before that.
  const basis = verb === 'scan' ? null : pressBasis({ modelId, roleModels }, verb, auditor)
  const whence = !basis
    ? ' An estimate at list rates that leans high on purpose.'
    : basis.passes > 0
      ? ` At list rates, from the last ${basis.passes} passes like it on ${basis.model} here: three in four of them drew no more.`
      : ` No passes like it have run on ${basis.model} here yet, so this is an allowance at list rates that leans high until a few have.`
  return (
    <span className="vp">
      {kbd && <span className="kbd vp-kbd">{kbd}</span>}
      <span
        className="vp-price"
        data-over={over || undefined}
        title={
          over
            ? 'This press is expected to carry the exploration past your cap. It will still run — the cap recolors, it does not block.'
            : `Drawn so far → expected after this press.${whence}`
        }
      >
        {fmtUsd(before)} → {fmtUsd(after)}
      </span>
    </span>
  )
}

/**
 * The SEVERAL PASSES header figure: what is left under the cap, or the warning
 * that the next multi-pass verb would cross it. Nothing without a cap — "left"
 * only means something against a limit somebody set.
 */
export function LeftMeta() {
  const spend = useStore((s) => s.session.spend)
  const rev = useStore((s) => s.rev)
  const modelId = useStore((s) => s.modelId)
  const roleModels = useStore((s) => s.roleModels)
  const runCeiling = useStore((s) => s.runCeiling)
  const live = useMemo(() => estimateSpendUsd(spend).usd, [spend, rev])
  // The figure below includes the voice's line while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  const cap = parseCap(runCeiling)
  if (cap === null) return null
  const left = cap - live
  const panel = projectVerbUsd({ modelId, roleModels }, 'panel')
  const exceeds = left <= 0 || (panel !== null && panel > left)
  if (exceeds) {
    return (
      <span
        className="vp-spent"
        data-over
        title="The next of these is expected to carry the exploration past your cap. It will still run — the cap recolors, it does not block."
      >
        {STATUS_WORD.wouldExceedCap}
      </span>
    )
  }
  return (
    <span
      className="vp-spent"
      title="What remains under your cap, at the estimate that leans high."
    >
      {fmtUsd(left)} left
    </span>
  )
}

/** The one hover line for the exploration's running figure, wherever it shows. */
export const SPEND_TITLE =
  'Estimated at list, leaning high — never a figure to reconcile against an invoice.'

/** The header figure for an action tier: what the exploration stands at, against
 *  the cap when one is set. Rides right-aligned beside an `.eyebrow`. Worded as
 *  an estimate ("est. … so far"), never as money "spent". */
export function SpentMeta() {
  const spend = useStore((s) => s.session.spend)
  const rev = useStore((s) => s.rev)
  const runCeiling = useStore((s) => s.runCeiling)
  const live = useMemo(() => estimateSpendUsd(spend), [spend, rev])
  const cap = parseCap(runCeiling)
  if (live.usd <= 0 && cap === null) return null
  const over = cap !== null && live.usd > cap
  return (
    <span
      className="vp-spent"
      data-over={over || undefined}
      title={
        live.unpriced.length
          ? `Estimated at list, leaning high. ${live.unpriced.length} model${
              live.unpriced.length === 1 ? ' has' : 's have'
            } no list rate here, so their draw is not in this figure.`
          : SPEND_TITLE
      }
    >
      {cap !== null
        ? `est. ${fmtUsd(live.usd)} of ${fmtUsd(cap)}`
        : `est. ${fmtUsd(live.usd)} so far`}
    </span>
  )
}
